/**
 * Pending public action registry — resolves a `PendingPublicAction`
 * (captured pre-sign-in on a public share link, see
 * @discovr/common's `pendingPublicAction` storage module) into a concrete,
 * per-type handler that knows how to:
 *   - re-validate AND locate the resumed action's current server state in
 *     ONE async step (`resolve`)
 *   - describe the confirm dialog copy (`confirmCopy`)
 *   - actually perform the write (`execute`)
 *
 * All three action types are implemented: `save_deliverables` (Task 3),
 * `approve_script` and `approve_content` (Task 4).
 *
 * ─── REWORK (final-review fix) — why this collapsed from 3 methods to 1 ────
 * The original design split this into `queryKeys()` (locate a cache entry),
 * `validate()` (read whatever landed there), and (for two-hop handlers)
 * `followUpQueryKeys()`. That depended on some OTHER component (the real
 * destination page) fetching the data into the cache first, and on the
 * caller (`usePendingPublicActionResume`) knowing when that fetch had
 * settled. In production neither held: `queryKeys()` ran with an empty
 * `campaignId` (the page hadn't populated the store yet), and a bare
 * `useQueries` observer with no `queryFn` doesn't sit at `pending` forever —
 * it fails fast ("Missing queryFn") and looks "settled" almost immediately,
 * so `validate()` ran against a cold, wrong cache. See the final review
 * report for the full failure chain.
 *
 * `resolve()` now OWNS its fetch via `queryClient.fetchQuery(...)`, calling
 * the exact same raw API function the real destination page's hook uses
 * underneath (`resolveCreatorByUsername` / `getScriptDetail` /
 * `getContentDetail`, all from `./api/campaignQueries`). `fetchQuery`
 * naturally dedups against a concurrent identical fetch the real page might
 * also be making — same cache-reuse goal the old prefix-scan approach had,
 * without depending on it.
 *
 * ─── Why campaignId (and campaignShortCode) come from a store, not a
 * parameter ──────────────────────────────────────────────────────────────
 * `PendingPublicAction` never carries the campaign's Mongo ObjectID — only
 * `campCode` (a short, lossy, slug-derived code used for the `/s/...` short
 * link) and `entryUsername`. Resolving campCode → ObjectID is an async BE
 * round trip; by the time `resolve()` runs, the visitor has already signed
 * in and landed back on the real campaign-detail page, which populates
 * `useCampaignUiStore` (a Zustand store, readable outside React via
 * `.getState()`) as its own data resolves. The hook that calls `resolve()`
 * waits for `campaignId` to be non-empty (reactively) before calling it —
 * see `usePendingPublicActionResume.ts`.
 *
 * ─── I1 fix — binding the resumed action to the campaign it was captured
 * on ────────────────────────────────────────────────────────────────────
 * Nothing previously checked that the campaign the visitor lands back on
 * (whatever `useCampaignUiStore` currently holds) is the SAME campaign the
 * action was captured on. A visitor who lands on a different campaign
 * within the 15-minute resume window (e.g. two share links open in the same
 * session) could otherwise validate/execute against the wrong campaign's
 * creator of the same username. `resolve()` now compares `action.campCode`
 * against `useCampaignUiStore.getState().campaignShortCode` up front, before
 * any fetch — a plain STRICT equality check (`!==`), no fail-open on an
 * empty store value.
 *
 * This used to fail open (treat "either side empty" as "not a mismatch")
 * because `campaignShortCode` looked like it could legitimately be empty:
 * once for legacy campaigns with no stored `short_code`, and once as a
 * timing race against `campaignId` loading first. Neither holds once
 * `CampaignDetailPage.tsx`'s effect seeds `campaignShortCode` — see that
 * file's `resolveCampaignShortCode(...)` call: it falls back to the same
 * client-derived `buildCampCode(slug)` the share-link builders already use
 * for legacy campaigns, so the store value is never empty for any campaign
 * that could ever have minted a `campCode` in the first place. And it's not
 * a race either: the `campaignId` PROP this page receives is itself derived
 * from `queries.campaign.data.id` (see
 * `apps/{brand,admin}/.../campaign-detail/hooks/index.ts`) — i.e. the SAME
 * full campaign-detail query response that carries `short_code`. By the
 * time `campaignId` is non-empty, that query has already resolved and its
 * data (including `short_code`) is sitting in the shared QueryClient cache;
 * `CampaignDetailPage`'s own `useCampaignDetail(campaignId)` call is a
 * cache hit, not a second network round trip, so `campaignId` and
 * `campaignShortCode` land in the store together, in the same effect flush.
 * A resumed action for a genuinely different campaign is therefore always a
 * real, known mismatch — strict equality is safe.
 *
 * ─── Cache-eviction fix — execute() no longer re-reads the QueryClient cache
 * ────────────────────────────────────────────────────────────────────────
 * `resolve()` populates the resolved roster-entry id into the QueryClient
 * via `fetchQuery` (no observer, so nothing keeps it "in use"). The
 * singleton QueryClient's `gcTime` is 5 minutes — if the user leaves the
 * confirm dialog open longer than that, the cache entry is garbage
 * collected. `execute()` used to re-read it via an exact-key
 * `getQueryData()` lookup and throw a raw internal message when it came back
 * empty — a developer-facing string (`"...call resolve() first."`) that
 * `errorMessage()` would then surface verbatim in a user-facing toast.
 *
 * `execute()` now takes `resolvedEntryId` as a parameter instead: `resolve()`
 * returns it on its `ok: true` result, and
 * `usePendingPublicActionResume.ts`'s hook carries that value forward
 * (in-memory, not the QueryClient cache) from the successful `resolve()`
 * call to the user's eventual confirm. `execute()` no longer reads the
 * QueryClient at all for this — its correctness no longer depends on
 * anything surviving `gcTime`.
 *
 * ─── Why approve_script / approve_content need a SECOND fetch ──────────────
 * `resolveCreatorByUsername`'s `creator.status` is NOT the production
 * (script/content) status — it's `entry.Status`, the pre-fan-out
 * negotiation status. Once a roster entry fans out into production,
 * `entry.Status` freezes at `accepted_by_creator` forever. Script/content
 * status lives on a separate record, fetched via `getScriptDetail` /
 * `getContentDetail` (`GET /roster/{id}/script` / `/content`), keyed off the
 * roster entry id the first fetch produces. So `resolve()` for these two
 * handlers is a sequential two-hop fetch: resolve username → entry id, then
 * entry id → script/content detail.
 */
import type { QueryClient } from '@tanstack/react-query';
import type { PendingPublicAction } from '@discovr/common';
import { ApiError } from '@discovr/common';
import type { ContentDetail, ScriptDetail } from '@discovr/types';
import { createCampaignDetailApi, type ResolveCreatorResult } from './api/campaignQueries';
import { createBrandMutationsApi } from './api/brandMutations';
import { campaignKeys, type RosterTab } from './queryKeys';
import { useCampaignUiStore, type RosterRowRef } from './state/useCampaignUiStore';
import {
  useCampaignCapabilitiesStore,
  type CampaignPermissions,
} from './state/useCampaignCapabilitiesStore';

const brandApi = createBrandMutationsApi();
const api = createCampaignDetailApi();

export type ValidationResult =
  // `resolvedEntryId` is OPTIONAL, not required — write actions (approve/
  // save) always set it (they replay against a specific roster entry); the
  // nav-intent actions (`navigate` / `select_pill`) have no entry to resolve
  // at all, so they succeed with it omitted. See `PendingActionHandler.execute`.
  | { ok: true; resolvedEntryId?: string }
  | {
      ok: false;
      reason: 'expired' | 'stale' | 'already_done' | 'not_found' | 'forbidden';
      message: string;
    };

const FORBIDDEN_MESSAGE =
  "You don't have permission to do this — signed in as a different role than expected.";
const NOT_FOUND_MESSAGE = "We couldn't find this creator in the campaign — the link may be out of date.";
const ALREADY_DONE_MESSAGE = 'This step has already been completed.';
const STALE_MESSAGE = 'This has changed since you started — please review the latest version before approving.';

const forbidden = (): ValidationResult => ({ ok: false, reason: 'forbidden', message: FORBIDDEN_MESSAGE });
const notFound = (): ValidationResult => ({ ok: false, reason: 'not_found', message: NOT_FOUND_MESSAGE });

/**
 * Minimal shape of the currently-authenticated identity needed to validate
 * a resumed action. Deliberately NOT Firebase's `User` — `@discovr/features`
 * has no direct dependency on `firebase/auth` (that lives in
 * `@discovr/common` / the apps), and no handler here needs more than a
 * presence check. Fine-grained authorization (can THIS user actually
 * perform the action) is a separate check against
 * `useCampaignCapabilitiesStore`, mirroring how this codebase already
 * splits identity (AuthContext) from authorization (capabilities store) —
 * see `save_deliverables`'s `resolve` below.
 */
export interface AuthUser {
  uid: string;
}

/**
 * Extra context `execute()` needs on top of the action + `resolvedEntryId` —
 * currently just the navigation escape hatch the `navigate` / `select_pill`
 * handlers use instead of an API call. Supplied by
 * `usePendingPublicActionResume.ts`'s caller: that hook lives in
 * `@discovr/features`, which has no dependency on `@tanstack/react-router`
 * (see that hook's own doc comment for the full dependency-direction
 * rationale — same reason it can't import `@discovr/ui`'s dialog directly),
 * so the actual `navigate()` call is built in `@discovr/ui`
 * (`PendingPublicActionResumeHost`, which DOES depend on the router) and
 * threaded down as a plain callback. Read by the nav-intent handlers
 * (navigate/select_pill) and also by approve_script/approve_content (since Task 1).
 */
export interface ExecuteCtx {
  /**
   * Navigates the resumed session to `target.tab` (and, for a resumed pill
   * click, `target.pill` within it). `creator`/`panel`/`sub` additionally
   * resume a resumed "View analysis" click (`open_insights`) — they mirror
   * the exact `?creator=`/`?panel=`/`?sub=` URL contract
   * `useCampaignPanelControls.openInsights` already writes, so the same
   * deep-link reader that opens the drawer from a shared URL opens it here
   * too, with no separate "open drawer" callback needed.
   */
  navigateToTab: (target: { tab: string; pill?: string; creator?: string; panel?: string; sub?: string }) => void;
}

export interface PendingActionHandler<A extends PendingPublicAction> {
  /**
   * Re-validates the resumed action against LIVE server data (never the
   * cache passively — this always issues its own `fetchQuery`) and, when
   * still valid, returns `{ ok: true, resolvedEntryId }`. Never throws — any
   * fetch failure (network error, 404, 403) is caught and turned into a
   * `ValidationResult`.
   */
  resolve(action: A, ctx: { queryClient: QueryClient; currentUser: AuthUser }): Promise<ValidationResult>;
  confirmCopy(action: A): { title: string; description: string };
  /**
   * `resolvedEntryId` is the roster-entry id `resolve()` returned on its
   * `ok: true` result — the caller (`usePendingPublicActionResume`) carries
   * it forward from the successful `resolve()` call instead of `execute()`
   * re-deriving it. See the module doc comment's "cache-eviction fix"
   * section for why `execute()` no longer reads it back out of the
   * QueryClient cache itself.
   *
   * Optional — the nav-intent handlers (`navigate` / `select_pill`) never
   * resolve one (there's no roster entry involved) and use `ctx` instead.
   *
   * `ctx` is optional: nav-intent handlers (navigate/select_pill) read
   * `ctx.navigateToTab` to navigate directly; approve_script/approve_content (since Task 1)
   * also read it to navigate back to the real approval panel instead of auto-executing.
   * save_deliverables ignores it. Keeping it optional means existing callers don't need
   * to pass ctx when not needed.
   */
  execute(action: A, resolvedEntryId: string | undefined, ctx?: ExecuteCtx): Promise<void>;
}

type SaveDeliverablesAction = Extract<PendingPublicAction, { type: 'save_deliverables' }>;
type ApproveScriptAction = Extract<PendingPublicAction, { type: 'approve_script' }>;
type ApproveContentAction = Extract<PendingPublicAction, { type: 'approve_content' }>;
type NavigateAction = Extract<PendingPublicAction, { type: 'navigate' }>;
type SelectPillAction = Extract<PendingPublicAction, { type: 'select_pill' }>;
type OpenInsightsAction = Extract<PendingPublicAction, { type: 'open_insights' }>;
type ApproveCreatorAction = Extract<PendingPublicAction, { type: 'approve_creator' }>;
type RejectAction = Extract<PendingPublicAction, { type: 'reject' }>;

/**
 * Fixed cache key `resolve()` fetches the creator resolution into — no
 * longer a prefix scan (the old code needed that to tolerate a caller-chosen
 * `pageSize` trailing segment it couldn't predict; now that this registry
 * owns the fetch outright, it just picks its own key deterministically).
 * Only used to populate/dedup the fetch itself — `execute()` no longer reads
 * this key back (see the module doc comment's "cache-eviction fix" section).
 */
function creatorResolutionKey(campaignId: string, username: string): readonly unknown[] {
  return [...campaignKeys.resolveCreator(campaignId, username), 'default'];
}

function scriptDetailKey(entryId: string): readonly unknown[] {
  return [...campaignKeys.scriptDetail(entryId), 0];
}

function contentDetailKey(entryId: string): readonly unknown[] {
  return [...campaignKeys.contentDetail(entryId), 0];
}

/**
 * Turns a `fetchQuery` rejection into a `ValidationResult`. A 401/403 means
 * the signed-in identity isn't allowed to read this resource at all — surface
 * that as `forbidden` rather than the generic `not_found`. Everything else
 * (404, network error, timeout) is `not_found`: from the visitor's point of
 * view, "we couldn't find this" and "we couldn't check" look the same.
 */
function classifyFetchError(err: unknown): ValidationResult {
  if (err instanceof ApiError && (err.httpStatus === 401 || err.httpStatus === 403)) {
    return forbidden();
  }
  return notFound();
}

type FetchOutcome<T> = { ok: true; data: T } | { ok: false; result: ValidationResult };

/**
 * Resolves a username to its roster entry via the SAME raw fetcher the real
 * shareable-link relanding hook (`useResolveCreatorByUsername`) uses
 * underneath (`api.resolveCreatorByUsername`, from `./api/campaignQueries`).
 * `fetchQuery` dedups against a concurrent identical fetch already in flight
 * on `qc` and never throws — failures come back as `{ ok: false }`.
 */
async function fetchCreatorResolution(
  qc: QueryClient,
  campaignId: string,
  username: string,
): Promise<FetchOutcome<ResolveCreatorResult>> {
  try {
    const data = await qc.fetchQuery({
      queryKey: creatorResolutionKey(campaignId, username),
      queryFn: () => api.resolveCreatorByUsername(campaignId, username),
    });
    return { ok: true, data };
  } catch (err) {
    return { ok: false, result: classifyFetchError(err) };
  }
}

/**
 * Compares the resumed action's captured `campCode` against the campaign
 * currently loaded into the store — strict equality. See the module doc
 * comment's I1 section for why `campaignShortCode` is guaranteed non-empty
 * (and loaded in step with `campaignId`) by the time this runs, so there's
 * no "not yet known" case left to fail open on.
 */
function campCodeMismatch(action: { campCode: string }): boolean {
  const { campaignShortCode } = useCampaignUiStore.getState();
  return campaignShortCode !== action.campCode;
}

/**
 * Clears the global drawer state (`deepAnalysisCreator` / `convCreator`) —
 * the store half of what `useCampaignPanelControls.closePanels()` does on
 * the authed app. `navigate`/`select_pill` resume land on a specific tab via
 * `ctx.navigateToTab`, which already clears the URL's `creator`/`panel`/
 * `sub` (it always assigns those keys, undefined when the action doesn't
 * carry them — see `PendingPublicActionResumeHost.tsx`'s `navigateToTab`).
 * But a drawer opened from an anonymous public view before the pending
 * action was captured lives in Zustand, not the URL — landing back on the
 * resumed tab with a clean URL left that drawer visually open on top of it,
 * since nothing had ever cleared the store side. `execute()` can't call
 * `closePanels()` itself (that hook needs `@tanstack/react-router`, which
 * this package deliberately doesn't depend on — see the module doc
 * comment), but the Zustand store needs no router, so it's cleared directly
 * here instead.
 */
function closeStaleDeepLinkDrawers(): void {
  const { setDeepAnalysisCreator, setConvCreator } = useCampaignUiStore.getState();
  setDeepAnalysisCreator(null);
  setConvCreator(null);
}

const saveDeliverablesHandler: PendingActionHandler<SaveDeliverablesAction> = {
  async resolve(action, ctx) {
    if (!ctx.currentUser) {
      return forbidden();
    }

    if (campCodeMismatch(action)) {
      return notFound();
    }

    // Deliverables-selection writes are gated on the caller's per-campaign
    // access level, not a `permissions.canX` flag — this is the exact same
    // check CommercialPopover itself uses to disable its Save button
    // (`isReadOnly = accessLevel === 'read'`; see that component's doc
    // comment: "Read-only members may open the popover and see deliverables
    // + prices, but cannot change the selection or save."). `null` (no
    // per-campaign grant resolved yet) fails closed for the same reason.
    const { accessLevel } = useCampaignCapabilitiesStore.getState();
    if (accessLevel === 'read' || accessLevel === null) {
      return forbidden();
    }

    const { campaignId } = useCampaignUiStore.getState();
    const fetched = await fetchCreatorResolution(ctx.queryClient, campaignId, action.entryUsername);
    if (!fetched.ok) {
      return fetched.result;
    }

    // RosterEntryCreators.status is 'pending' | 'accepted_by_brand' |
    // 'accepted_by_admin' — deliverables selection is only meaningful while
    // the entry is still 'pending' (the Creators tab's pending pill, where
    // CommercialPopover's Save button lives). Once the brand or admin has
    // moved it forward, the selection captured pre-sign-in is stale.
    if (fetched.data.creator.status !== 'pending') {
      return { ok: false, reason: 'already_done', message: ALREADY_DONE_MESSAGE };
    }

    return { ok: true, resolvedEntryId: fetched.data.creator.id };
  },

  confirmCopy() {
    return {
      title: 'Confirm your deliverable selection',
      description: 'You selected these deliverables before signing in — save this selection now?',
    };
  },

  async execute(action, resolvedEntryId, ctx) {
    if (!resolvedEntryId) {
      throw new Error('[pendingPublicActionRegistry] resolvedEntryId missing for save_deliverables');
    }
    // No longer mutates directly — writes the resumed selection into the
    // store and lets CreatorsTab's own effect (packages/ui) reopen the real
    // CommercialPopover, pre-filled, so the visitor's own click on its real
    // Save button is the one confirmation. See the spec's D3.
    useCampaignUiStore.getState().setCommercialPopoverRequest({
      entryId: resolvedEntryId,
      selectedMainIds: action.selectedMainIds,
      selectedAddonIds: action.selectedAddonIds,
    });
    ctx?.navigateToTab({ tab: 'creators', pill: 'pending' });
  },
};

/**
 * Shared shape between `approve_script` and `approve_content` — both are
 * "brand approves a submitted post" actions that differ only in which
 * detail fetch/status/permission/endpoint they use. Factored into one
 * generic builder instead of two near-duplicate handler literals.
 */
interface ApprovePhaseConfig<A extends ApproveScriptAction | ApproveContentAction> {
  /** The RosterTab this action's authed panel lives on — used to replicate
   *  the authed handler's extra `campaignKeys.rosterTab(...)` invalidation
   *  (see ScriptsTab.tsx's/ContentTab.tsx's handleApprove* — invalidates
   *  this on top of the mutation's own rosterAll() invalidate). */
  rosterTab: RosterTab;
  /** `campaignKeys.scriptDetail` / `campaignKeys.contentDetail`, plus the
   *  version-0 cache-bust segment — see `scriptDetailKey`/`contentDetailKey`. */
  detailQueryKey: (entryId: string) => readonly unknown[];
  /** The SAME raw fetcher the real destination page's hook
   *  (`useScriptDetail` / `useContentDetail`) uses underneath —
   *  `api.getScriptDetail` / `api.getContentDetail`. */
  detailFetcher: (entryId: string) => Promise<ScriptDetail | ContentDetail>;
  /** The roster_posts status this action is valid to run from —
   *  'script_review' / 'content_review'. Any other status means the
   *  resumed approval is no longer actionable (already approved, or the
   *  post was never uploaded / reverted). */
  approvableStatus: string;
  /** `permissions.canApproveScript` / `permissions.canReviewContent` — see
   *  the module doc comment: this is a `CampaignPermissions` flag, NOT the
   *  `accessLevel` gate save_deliverables uses. Brand-only in both cases;
   *  admin's flag is always false (BE-enforced too — see brandMutations.ts). */
  hasPermission: (permissions: CampaignPermissions | null) => boolean;
  /** The exact authed mutation call — `brandApi.approveScript` /
   *  `brandApi.approveContent` (single-element array, matching how
   *  ScriptsTab/ContentTab's handleApprove* call it for one entry at a time). */
  callApi: (rosterEntryId: string) => Promise<unknown>;
  confirmCopy: (action: A) => { title: string; description: string };
  /**
   * Where `execute()` sends the resumed visitor instead of firing `callApi`
   * directly — the real ScriptReviewPanel/ContentReviewPanel already has an
   * Approve button; landing here (via the SAME `?tab=&pill=&creator=&panel=`
   * deep-link contract `useCampaignDeepLinkPanel.ts` reads) reopens it
   * pre-filled, so the visitor's own click on that real button is the one
   * confirmation — see the spec's D3.
   */
  navigateTarget: { tab: RosterTab; pill: string; panel: string };
}

function createApprovePhaseHandler<A extends ApproveScriptAction | ApproveContentAction>(
  config: ApprovePhaseConfig<A>,
): PendingActionHandler<A> {
  async function fetchDetail(qc: QueryClient, entryId: string): Promise<FetchOutcome<ScriptDetail | ContentDetail>> {
    try {
      const data = await qc.fetchQuery({
        queryKey: config.detailQueryKey(entryId),
        queryFn: () => config.detailFetcher(entryId),
      });
      return { ok: true, data };
    } catch (err) {
      return { ok: false, result: classifyFetchError(err) };
    }
  }

  return {
    confirmCopy: config.confirmCopy,

    async resolve(action, ctx) {
      if (!ctx.currentUser) {
        return forbidden();
      }

      if (campCodeMismatch(action)) {
        return notFound();
      }

      // Brand-only, gated on the CampaignPermissions flag the authed panel
      // itself gates on (ScriptReviewPanel's `canReview` / ContentReviewPanel's
      // `canReview` — see the module doc comment for why this differs from
      // save_deliverables' `accessLevel` check).
      const { permissions } = useCampaignCapabilitiesStore.getState();
      if (!config.hasPermission(permissions)) {
        return forbidden();
      }

      const { campaignId } = useCampaignUiStore.getState();
      const fetchedCreator = await fetchCreatorResolution(ctx.queryClient, campaignId, action.entryUsername);
      if (!fetchedCreator.ok) {
        return fetchedCreator.result;
      }

      // Second fetch — see the module doc comment: resolveCreator's status is
      // NOT the script/content phase status, so the actual approvable-stage
      // + version check reads the roster_posts-backed detail record instead.
      const detail = await fetchDetail(ctx.queryClient, fetchedCreator.data.creator.id);
      if (!detail.ok) {
        return detail.result;
      }

      // `detail.data.status` is the narrower pending/approved flag on the
      // script/content sub-document itself (roster.domain.ReviewStatus on the
      // BE) — it never equals 'script_review'/'content_review'. `post_status`
      // is the roster post's actual workflow phase, the same field the authed
      // panels gate their own Approve button on (ScriptsTab/ContentTab's
      // `dc.status`). Comparing against `status` here would make this check
      // permanently unsatisfiable.
      if (detail.data.post_status !== config.approvableStatus) {
        return { ok: false, reason: 'already_done', message: ALREADY_DONE_MESSAGE };
      }

      if (detail.data.version !== action.version) {
        return { ok: false, reason: 'stale', message: STALE_MESSAGE };
      }

      return { ok: true, resolvedEntryId: fetchedCreator.data.creator.id };
    },

    async execute(action, _resolvedEntryId, ctx) {
      // No longer fires config.callApi directly — see the module doc
      // comment's D3 note. resolvedEntryId is still returned by resolve()
      // (used for the resolvedEntryId-not-undefined invariant elsewhere) but
      // execute() no longer needs it: the real panel resolves its own entry
      // from `?creator=` once it opens.
      if (!_resolvedEntryId) {
        throw new Error(`[pendingPublicActionRegistry] resolvedEntryId missing for ${action.type}`);
      }
      ctx?.navigateToTab({ ...config.navigateTarget, creator: action.entryUsername });
    },
  };
}

const approveScriptHandler: PendingActionHandler<ApproveScriptAction> = createApprovePhaseHandler({
  rosterTab: 'scripts',
  detailQueryKey: scriptDetailKey,
  detailFetcher: (entryId) => api.getScriptDetail(entryId),
  approvableStatus: 'script_review',
  hasPermission: (permissions) => !!permissions?.canApproveScript,
  callApi: (rosterEntryId) => brandApi.approveScript(rosterEntryId),
  navigateTarget: { tab: 'scripts', pill: 'review', panel: 'script-review' },
  confirmCopy: () => ({
    title: 'Continue approving this script?',
    description: 'Sign-in confirmed — you\'ll land back on this script to approve it for real.',
  }),
});

const approveContentHandler: PendingActionHandler<ApproveContentAction> = createApprovePhaseHandler({
  rosterTab: 'content',
  detailQueryKey: contentDetailKey,
  detailFetcher: (entryId) => api.getContentDetail(entryId),
  approvableStatus: 'content_review',
  // Content approval is gated on canReviewContent, not a separate
  // canApproveContent flag — see packages/ui/src/lib/campaignPermissions.ts
  // (buildBrandPermissions/buildAdminPermissions): brand's canReviewContent
  // is canWrite, admin's is hardcoded false ("admin never approves content,
  // brand only"). ContentTab.tsx's canReviewThisContent gate matches this.
  hasPermission: (permissions) => !!permissions?.canReviewContent,
  callApi: (rosterEntryId) => brandApi.approveContent([rosterEntryId]),
  navigateTarget: { tab: 'content', pill: 'review', panel: 'content-review' },
  confirmCopy: () => ({
    title: 'Continue approving this content?',
    description: 'Sign-in confirmed — you\'ll land back on this content to approve it for real.',
  }),
});

/**
 * Turns a raw tab/pill id ('live-content', 'accepted_by_brand') into a
 * human-readable label ('Live content', 'Accepted by brand') for the confirm
 * dialog's copy. Deliberately NOT importing the real nav-item label map
 * (`buildNavItems` in `@discovr/ui`) — this registry lives in
 * `@discovr/features`, which `@discovr/ui` depends ON, not the reverse (see
 * the module doc comment's dependency-direction note on the write handlers
 * above). A plain slug-to-title-case humanizer is a fine approximation for a
 * one-line confirmation prompt and keeps this file dependency-free.
 */
function humanizeSlug(slug: string): string {
  const [first, ...rest] = slug.split(/[-_]/).filter(Boolean);
  if (!first) return slug;
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ');
}

/**
 * `navigate` / `select_pill` — resumes a visitor's click on a sidebar nav
 * item or a status pill that wasn't covered by their share link, captured
 * pre-sign-in (see `PublicCampaignShell.tsx` / `CreatorsTab.tsx`'s
 * `onRequestLogin` wiring).
 *
 * Unlike the three write handlers above, there is no roster entry to
 * re-validate against live server data — the only thing that could make a
 * resumed nav intent stale is landing back on a DIFFERENT campaign than the
 * one it was captured on (same `campCodeMismatch` check the write handlers
 * use), so `resolve()` here is synchronous and never touches the network.
 * `execute()` performs the actual client-side navigation via `ctx.navigateToTab`
 * instead of an API call — see `ExecuteCtx`'s doc comment for why that
 * function is injected rather than called directly (this package has no
 * `@tanstack/react-router` dependency).
 */
const navigateHandler: PendingActionHandler<NavigateAction> = {
  resolve(action, ctx) {
    if (!ctx.currentUser) return Promise.resolve(forbidden());
    if (campCodeMismatch(action)) return Promise.resolve(notFound());
    return Promise.resolve({ ok: true });
  },

  confirmCopy(action) {
    const label = humanizeSlug(action.tab);
    return {
      title: `Go to ${label}?`,
      description: `You clicked ${label} before signing in — go there now?`,
    };
  },

  async execute(action, _resolvedEntryId, ctx) {
    // `ctx` is undefined only when a caller invokes this without one (e.g. a
    // test exercising the write-handler call convention) — the real caller
    // (`usePendingPublicActionResume.ts`) always supplies it. Nothing to do
    // without a navigation mechanism.
    closeStaleDeepLinkDrawers();
    ctx?.navigateToTab({ tab: action.tab });
  },
};

const selectPillHandler: PendingActionHandler<SelectPillAction> = {
  resolve(action, ctx) {
    if (!ctx.currentUser) return Promise.resolve(forbidden());
    if (campCodeMismatch(action)) return Promise.resolve(notFound());
    return Promise.resolve({ ok: true });
  },

  confirmCopy(action) {
    const label = humanizeSlug(action.pill);
    return {
      title: `Go to ${label}?`,
      description: `You clicked ${label} before signing in — go there now?`,
    };
  },

  async execute(action, _resolvedEntryId, ctx) {
    // See navigateHandler's identical guard above.
    closeStaleDeepLinkDrawers();
    ctx?.navigateToTab({ tab: action.tab, pill: action.pill });
  },
};

/**
 * `open_insights` — resumes a visitor's click on a roster row's "View
 * analysis" affordance (opens the Insights/Brand-Safety drawer for a
 * specific creator), captured pre-sign-in from a roster-tab public grant
 * (creators/confirmed) — see CreatorsTab.tsx's/ConfirmedTab.tsx's
 * `handlePublicViewInsights`.
 *
 * Unlike `navigate`/`select_pill`, this IS scoped to one creator, so
 * `resolve()` re-validates that the creator still exists in the campaign the
 * same way the write handlers do (`fetchCreatorResolution`) — a creator
 * removed between the click and sign-in should fail with `not_found` rather
 * than land the visitor on an empty drawer. There's no status/permission
 * gate beyond that: viewing analysis has none in the authed app either (see
 * `CreatorAnalysisButton`'s own doc comment — no `canX` check gates it).
 *
 * `execute()` doesn't open the drawer directly — it reuses the exact
 * `?creator=`/`?panel=`/`?sub=` URL contract `useCampaignPanelControls`'s
 * `openInsights` already writes (see `ExecuteCtx.navigateToTab`'s doc
 * comment), so the page's own deep-link reader opens the drawer once the
 * resumed navigation lands — no bespoke "open drawer" callback needed.
 */
const openInsightsHandler: PendingActionHandler<OpenInsightsAction> = {
  async resolve(action, ctx) {
    if (!ctx.currentUser) {
      return forbidden();
    }

    if (campCodeMismatch(action)) {
      return notFound();
    }

    const { campaignId } = useCampaignUiStore.getState();
    const fetched = await fetchCreatorResolution(ctx.queryClient, campaignId, action.entryUsername);
    if (!fetched.ok) {
      return fetched.result;
    }

    return { ok: true, resolvedEntryId: fetched.data.creator.id };
  },

  confirmCopy() {
    return {
      title: "View this creator's analysis?",
      description: "You opened this creator's analysis before signing in — view it now?",
    };
  },

  async execute(action, _resolvedEntryId, ctx) {
    // Mirrors useCampaignPanelControls.openInsights's own panel/sub mapping
    // exactly, so the resumed URL matches what a direct authed click would
    // have written.
    const insightsTab = action.insightsTab ?? 'overview';
    const isBrandSafety = insightsTab === 'brand-safety';
    ctx?.navigateToTab({
      tab: action.tab,
      creator: action.entryUsername,
      panel: isBrandSafety ? 'brand-safety' : 'insights',
      sub: !isBrandSafety && insightsTab !== 'overview' ? insightsTab : undefined,
    });
  },
};

/**
 * `approve_creator` / `reject` — resume a click on the Creators roster
 * row's own Approve/Reject buttons (exposed in public mode alongside this
 * feature — see CreatorsTab.tsx's permission-flip for `isPublicView`).
 * Reuses the EXISTING `approvalRequest`/`rejectRequest` signal on
 * `useCampaignUiStore` — the same mechanism the Insights drawer's own
 * Approve/Reject buttons already use to open `ApprovalCard` pre-filled —
 * so `execute()` here is a plain store write, not a mutation.
 */
function createRosterSignalHandler<A extends ApproveCreatorAction | RejectAction>(
  config: {
    actionLabel: string;
    setSignal: (id: string) => void;
    confirmCopy: () => { title: string; description: string };
  },
): PendingActionHandler<A> {
  return {
    async resolve(action, ctx) {
      if (!ctx.currentUser) {
        return forbidden();
      }
      if (campCodeMismatch(action)) {
        return notFound();
      }

      // Same CampaignPermissions gate createApprovePhaseHandler's siblings
      // enforce (see this module's doc comment). Without it, a real
      // signed-in brand_member with canApproveRejectCreators:false — the
      // exact read-only role CreatorsTab.tsx's isPublicView flip exists to
      // NOT apply to a genuinely authed user — gets a fully working resumed
      // dialog that only fails server-side on confirm, instead of the same
      // client-side deny every sibling resume action gives that role.
      const { permissions } = useCampaignCapabilitiesStore.getState();
      if (!permissions?.canApproveRejectCreators) {
        return forbidden();
      }

      const { campaignId } = useCampaignUiStore.getState();
      const fetched = await fetchCreatorResolution(ctx.queryClient, campaignId, action.entryUsername);
      if (!fetched.ok) {
        return fetched.result;
      }
      if (fetched.data.creator.status !== 'pending') {
        return { ok: false, reason: 'already_done', message: ALREADY_DONE_MESSAGE };
      }
      return { ok: true, resolvedEntryId: fetched.data.creator.id };
    },

    confirmCopy: config.confirmCopy,

    async execute(action, resolvedEntryId, ctx) {
      if (!resolvedEntryId) {
        throw new Error(`[pendingPublicActionRegistry] resolvedEntryId missing for ${config.actionLabel}`);
      }
      config.setSignal(resolvedEntryId);
      ctx?.navigateToTab({ tab: 'creators', pill: 'pending' });
    },
  };
}

const approveCreatorHandler: PendingActionHandler<ApproveCreatorAction> = createRosterSignalHandler({
  actionLabel: 'approve_creator',
  setSignal: (id) => useCampaignUiStore.getState().setApprovalRequest({ id } as unknown as RosterRowRef),
  confirmCopy: () => ({
    title: 'Continue approving this creator?',
    description: "You clicked Approve before signing in — you'll land back on this row to approve it for real.",
  }),
});

const rejectHandler: PendingActionHandler<RejectAction> = createRosterSignalHandler({
  actionLabel: 'reject',
  setSignal: (id) => useCampaignUiStore.getState().setRejectRequest({ id } as unknown as RosterRowRef),
  confirmCopy: () => ({
    title: 'Continue rejecting this creator?',
    description: "You clicked Reject before signing in — you'll land back on this row to reject it for real.",
  }),
});

export const PENDING_ACTION_HANDLERS: {
  approve_script: PendingActionHandler<ApproveScriptAction>;
  approve_content: PendingActionHandler<ApproveContentAction>;
  save_deliverables: PendingActionHandler<SaveDeliverablesAction>;
  navigate: PendingActionHandler<NavigateAction>;
  select_pill: PendingActionHandler<SelectPillAction>;
  open_insights: PendingActionHandler<OpenInsightsAction>;
  approve_creator: PendingActionHandler<ApproveCreatorAction>;
  reject: PendingActionHandler<RejectAction>;
} = {
  approve_script: approveScriptHandler,
  approve_content: approveContentHandler,
  save_deliverables: saveDeliverablesHandler,
  navigate: navigateHandler,
  select_pill: selectPillHandler,
  open_insights: openInsightsHandler,
  approve_creator: approveCreatorHandler,
  reject: rejectHandler,
};
